import type { ShortcutContext, ShortcutScopeHandle, ShortcutScopeOptions } from 'src/stores/shortcuts/types';

import { type Ref, computed, ref, shallowRef } from 'vue';

import { useShortcutsStore } from 'src/stores/shortcuts';

import { SHORTCUT_ACTIONS_BY_ID } from 'src/util/shortcuts/actionsRegistry';
import { isSingleLetterKeys, keysFromKeyboardEvent } from 'src/util/shortcuts/keyNormalizer';
import {
	type ResolvableScope,
	resolveActiveContexts,
	resolveDispatchScope,
	shouldBypassEditorGate,
} from 'src/util/shortcuts/scopeResolver';

type ShortcutHandler = (event: KeyboardEvent) => void;

interface RegisteredHandler {
	actionId: string;
	handler: ShortcutHandler;
}

interface InternalScope {
	/** Nur der interne Global-Scope ist elementlos immer aktiv; Komponenten-Scopes ohne Element sind unsichtbar (fail-safe). */
	alwaysVisible: boolean;
	context: ShortcutContext;
	element: () => HTMLElement | null;
	handlers: Record<string, ShortcutHandler>;
	lastInteractionOrder: number;
	registrationOrder: number;
}

const MODAL_MASK_SELECTOR = '.p-overlay-mask';
const MODAL_EXEMPT_ACTION_ID = 'app.cheatsheet.toggle';

const scopesRef = shallowRef<InternalScope[]>([]);
const scopesVersionRef = ref(0);

let registrationCounter = 0;
let interactionCounter = 0;

const CHORD_TIMEOUT_MS = 1500;

let chordPrefix: null | string = null;
let chordTimeoutHandle: null | number = null;
let keydownAttached = false;
let suspensionCount = 0;

export function suspendShortcuts(): void {
	suspensionCount += 1;
}

export function resumeShortcuts(): void {
	if (suspensionCount > 0) {
		suspensionCount -= 1;
	}
}

function isSuspended(): boolean {
	return suspensionCount > 0;
}

function clearChord(): void {
	chordPrefix = null;

	if (chordTimeoutHandle !== null) {
		window.clearTimeout(chordTimeoutHandle);
		chordTimeoutHandle = null;
	}
}

const EDITOR_TARGET_SELECTOR =
	'.tox-edit-area, .tox-tinymce, .fr-element, .fr-wrapper, .tiptap-content, .jodit-wysiwyg, .sun-editor-editable, .ProseMirror';

function isEditorTarget(target: EventTarget | null): boolean {
	if (target === null || !(target instanceof HTMLElement)) {
		return false;
	}

	if (target.closest(EDITOR_TARGET_SELECTOR) !== null) {
		return true;
	}

	return target.isContentEditable;
}

function isTypingTarget(target: EventTarget | null): boolean {
	if (target === null || !(target instanceof HTMLElement)) {
		return false;
	}

	if (isEditorTarget(target)) {
		return true;
	}

	const tagName = target.tagName;

	return tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
}

function isElementVisible(element: HTMLElement): boolean {
	if (typeof element.checkVisibility === 'function') {
		return element.checkVisibility();
	}

	return element.offsetParent !== null;
}

function topmostOpenModal(): HTMLElement | null {
	if (typeof document === 'undefined') {
		return null;
	}

	const masks = document.querySelectorAll<HTMLElement>(MODAL_MASK_SELECTOR);

	return masks.length > 0 ? (masks[masks.length - 1] ?? null) : null;
}

interface ScopeSnapshotEntry extends ResolvableScope {
	scope: InternalScope;
}

function buildScopeSnapshot(): { entries: ScopeSnapshotEntry[]; modalOpen: boolean } {
	const modalElement = topmostOpenModal();
	const activeElement = typeof document !== 'undefined' ? document.activeElement : null;

	const entries = scopesRef.value.map((scope): ScopeSnapshotEntry => {
		const element = scope.element();
		const visible = scope.alwaysVisible || (element !== null && isElementVisible(element));
		const containsFocus =
			element !== null && activeElement instanceof HTMLElement ? element.contains(activeElement) : false;
		const insideOpenModal = element === null ? false : modalElement !== null && modalElement.contains(element);

		return {
			containsFocus,
			context: scope.context,
			handledActionIds: new Set(Object.keys(scope.handlers)),
			insideOpenModal,
			lastInteractionOrder: scope.lastInteractionOrder,
			registrationOrder: scope.registrationOrder,
			scope,
			visible,
		};
	});

	return { entries, modalOpen: modalElement !== null };
}

function currentActiveContexts(): ShortcutContext[] {
	const { entries } = buildScopeSnapshot();

	return resolveActiveContexts(entries);
}

/**
 * Reaktive Sicht auf die aktiven Kontexte (für Cheatsheet u. ä.).
 * Recomputed bei Scope-Registrierung/-Dispose und Nutzer-Interaktion.
 */
export const activeShortcutContexts: Readonly<Ref<ShortcutContext[]>> = computed(() => {
	void scopesVersionRef.value;

	return currentActiveContexts();
});

function actionMatchesContext(actionContexts: ShortcutContext[], activeStack: ShortcutContext[]): boolean {
	return actionContexts.some((context) => activeStack.includes(context));
}

function resolveBindingMatch(keys: string, activeStack: ShortcutContext[]): null | string {
	const store = useShortcutsStore();
	const candidates: string[] = [];

	for (const binding of Object.values(store.bindings)) {
		if (!binding.enabled || binding.keys.length === 0 || binding.keys !== keys) {
			continue;
		}

		const action = SHORTCUT_ACTIONS_BY_ID[binding.id];

		if (action === undefined || !actionMatchesContext(action.contexts, activeStack)) {
			continue;
		}

		candidates.push(binding.id);
	}

	if (candidates.length === 0) {
		return null;
	}

	return (
		candidates.sort((left, right) => {
			const leftAction = SHORTCUT_ACTIONS_BY_ID[left];
			const rightAction = SHORTCUT_ACTIONS_BY_ID[right];

			if (leftAction === undefined || rightAction === undefined) {
				return 0;
			}

			const priority: Record<string, number> = {
				detail: 3,
				global: 0,
				list: 2,
				navigation: 1,
			};

			return (priority[rightAction.category] ?? 0) - (priority[leftAction.category] ?? 0);
		})[0] ?? null
	);
}

function findBindingByKeysPrefix(prefix: string): boolean {
	const store = useShortcutsStore();

	for (const binding of Object.values(store.bindings)) {
		if (!binding.enabled || binding.keys.length === 0) {
			continue;
		}

		if (binding.keys === prefix) {
			continue;
		}

		if (binding.keys.startsWith(`${prefix} `)) {
			return true;
		}
	}

	return false;
}

function dispatchAction(actionId: string, event: KeyboardEvent): boolean {
	const { entries, modalOpen } = buildScopeSnapshot();
	const effectiveModalOpen = modalOpen && actionId !== MODAL_EXEMPT_ACTION_ID;
	const target = resolveDispatchScope(entries, actionId, effectiveModalOpen);

	if (target === null) {
		return false;
	}

	const handler = (target as ScopeSnapshotEntry).scope.handlers[actionId];

	if (handler === undefined) {
		return false;
	}

	handler(event);
	return true;
}

function handleKeydown(event: KeyboardEvent): void {
	if (isSuspended()) {
		return;
	}

	const store = useShortcutsStore();

	if (!store.enabled) {
		return;
	}

	const editorTarget = isEditorTarget(event.target);

	const activeStack = currentActiveContexts();

	const keys = keysFromKeyboardEvent(event);

	if (keys.length === 0) {
		return;
	}

	if (isTypingTarget(event.target) && isSingleLetterKeys(keys)) {
		return;
	}

	let effectiveKeys = keys;

	if (chordPrefix !== null && !editorTarget) {
		effectiveKeys = `${chordPrefix} ${keys}`;
	}

	const matchedActionId = resolveBindingMatch(effectiveKeys, activeStack);

	if (matchedActionId !== null) {
		if (editorTarget && !shouldBypassEditorGate(SHORTCUT_ACTIONS_BY_ID[matchedActionId], effectiveKeys)) {
			return;
		}

		clearChord();

		if (dispatchAction(matchedActionId, event)) {
			event.preventDefault();
			event.stopPropagation();
		}

		return;
	}

	if (editorTarget) {
		return;
	}

	if (chordPrefix !== null) {
		clearChord();
		return;
	}

	if (findBindingByKeysPrefix(keys)) {
		event.preventDefault();
		chordPrefix = keys;
		chordTimeoutHandle = window.setTimeout(() => {
			clearChord();
		}, CHORD_TIMEOUT_MS);
	}
}

function handleInteraction(event: Event): void {
	const target = event.target;

	if (!(target instanceof Node)) {
		return;
	}

	let interacted = false;

	for (const scope of scopesRef.value) {
		const element = scope.element();

		if (element !== null && element.contains(target)) {
			scope.lastInteractionOrder = ++interactionCounter;
			interacted = true;
		}
	}

	if (interacted) {
		scopesVersionRef.value += 1;
	}
}

function registerScope(options: ShortcutScopeOptions): ShortcutScopeHandle {
	const scope: InternalScope = {
		alwaysVisible: false,
		context: options.context,
		element: options.element,
		handlers: { ...options.handlers },
		lastInteractionOrder: -1,
		registrationOrder: ++registrationCounter,
	};

	scopesRef.value = [...scopesRef.value, scope];
	scopesVersionRef.value += 1;

	return {
		dispose: (): void => {
			scopesRef.value = scopesRef.value.filter((entry) => entry !== scope);
			scopesVersionRef.value += 1;
		},
	};
}

const globalScope: InternalScope = {
	alwaysVisible: true,
	context: 'global',
	element: () => null,
	handlers: {},
	lastInteractionOrder: -1,
	registrationOrder: 0,
};

if (scopesRef.value.length === 0) {
	scopesRef.value = [globalScope];
}

interface ShortcutRegistryApi {
	registerHandler: (actionId: string, handler: ShortcutHandler) => void;
	registerHandlers: (handlers: RegisteredHandler[]) => void;
	registerScope: (options: ShortcutScopeOptions) => ShortcutScopeHandle;
	unregisterHandler: (actionId: string) => void;
}

function registerHandler(actionId: string, handler: ShortcutHandler): void {
	globalScope.handlers[actionId] = handler;
	scopesVersionRef.value += 1;
}

function registerHandlers(handlers: RegisteredHandler[]): void {
	for (const entry of handlers) {
		globalScope.handlers[entry.actionId] = entry.handler;
	}

	scopesVersionRef.value += 1;
}

function unregisterHandler(actionId: string): void {
	delete globalScope.handlers[actionId];
	scopesVersionRef.value += 1;
}

export function attachShortcutListener(): void {
	if (keydownAttached || typeof document === 'undefined') {
		return;
	}

	document.addEventListener('keydown', handleKeydown, true);
	document.addEventListener('pointerdown', handleInteraction, true);
	document.addEventListener('focusin', handleInteraction, true);
	keydownAttached = true;
}

export function detachShortcutListener(): void {
	if (!keydownAttached || typeof document === 'undefined') {
		return;
	}

	document.removeEventListener('keydown', handleKeydown, true);
	document.removeEventListener('pointerdown', handleInteraction, true);
	document.removeEventListener('focusin', handleInteraction, true);
	keydownAttached = false;
	clearChord();
}

export function useShortcutRegistry(): ShortcutRegistryApi {
	return {
		registerHandler,
		registerHandlers,
		registerScope,
		unregisterHandler,
	};
}
